import { useRef } from 'react';
import {
  FrozenDateSelectionUiSnapshot,
  UseFrozenDateSelectionUiParams,
  UseFrozenDateSelectionUiResult,
} from '@msc-b2c/interfaces/interfaces';

/**
 * Freezes date/time UI inputs while confirmation is in progress.
 *
 * This prevents transient state updates from parent async flows from changing
 * selected date/time/restaurant options during the lock window.
 */
export const useFrozenDateSelectionUi = ({
  isInteractionLocked,
  selectedDate,
  selectedRestaurantCode,
  selectedTime,
  restaurantOptions,
  timeslots,
  selectedDays,
}: UseFrozenDateSelectionUiParams): UseFrozenDateSelectionUiResult => {
  const wasInteractionLockedRef = useRef(false);
  const frozenUiRef = useRef<FrozenDateSelectionUiSnapshot | null>(null);

  if (isInteractionLocked && !wasInteractionLockedRef.current) {
    frozenUiRef.current = {
      selectedDate,
      selectedRestaurantCode,
      selectedTime,
      restaurantOptions: restaurantOptions?.map((restaurant) => ({
        ...restaurant,
        timeslots: [...restaurant.timeslots],
        conflictTimes: restaurant.conflictTimes ? [...restaurant.conflictTimes] : undefined,
      })),
      timeslots: timeslots ? [...timeslots] : undefined,
      selectedDays: [...selectedDays],
    };
  }

  if (!isInteractionLocked && wasInteractionLockedRef.current) {
    frozenUiRef.current = null;
  }

  wasInteractionLockedRef.current = isInteractionLocked;

  return {
    effectiveSelectedDate: frozenUiRef.current?.selectedDate ?? selectedDate,
    effectiveSelectedRestaurantCode:
      frozenUiRef.current?.selectedRestaurantCode ?? selectedRestaurantCode,
    effectiveSelectedTime: frozenUiRef.current?.selectedTime ?? selectedTime,
    effectiveRestaurantOptions: frozenUiRef.current?.restaurantOptions ?? restaurantOptions,
    effectiveTimeslots: frozenUiRef.current?.timeslots ?? timeslots,
    effectiveSelectedDays: frozenUiRef.current?.selectedDays ?? selectedDays,
  };
};
